import type {
  AssignClaimsToAuditGroupInput,
  ClaimAuditGroupPatch,
  CreateClaimAuditGroupCommentInput,
  CreateClaimAuditGroupInput,
  AuditGroupCommentsQuery,
  deleteClaimAuditGroupMutation,
  UnassignClaimsFromAuditGroupInput,
  assignClaimsToAuditGroupMutation,
  CreateClaimAuditGroupCommentMutation,
  createClaimAuditGroupMutation,
  createReviewedAuditFindingMutation,
  getAuditFindingRuleTypesQuery,
  getClaimAuditGroupsQuery,
  getClaimAuditGroupsQueryVariables,
  ReviewedAuditFindingInput,
  unassignClaimsFromAuditGroupMutation,
  updateClaimAuditGroupMutation,
} from "../../remote/gql/graphql";
import type { ClientReturnType } from "../types";
import type { AlaffiaClientModuleArgs } from "./modules";

import { assignClaimsToAuditGroupMutationDoc } from "../../remote/mutations/assignClaimsToAuditGroup.gql";
import { createClaimAuditGroupMutationDoc } from "../../remote/mutations/createClaimAuditGroup.gql";
import { createClaimAuditGroupCommentMutationDoc } from "../../remote/mutations/createClaimAuditGroupComment.gql";
import { createReviewedAuditFindingMutationDoc } from "../../remote/mutations/createReviewedAuditFinding.gql";
import { deleteClaimAuditGroupMutationDoc } from "../../remote/mutations/deleteClaimAuditGroup.gql";
import { unassignClaimsFromAuditGroupMutationDoc } from "../../remote/mutations/unassignClaimsFromAuditGroup.gql";
import { updateClaimAuditGroupMutationDoc } from "../../remote/mutations/updateClaimAuditGroup.gql";
import { getAuditFindingRuleTypesQueryDoc } from "../../remote/queries/getAuditFindingRuleTypes.gql";
import { getClaimAuditGroupCommentsQueryDoc } from "../../remote/queries/getClaimAuditGroupComments.gql";
import { getClaimAuditGroupsQueryDoc } from "../../remote/queries/getClaimAuditGroups.gql";
import { AlaffiaClientModule } from "./modules";

type AuditsModuleArgs = AlaffiaClientModuleArgs;

export class AuditsModule extends AlaffiaClientModule {
  constructor(args: AuditsModuleArgs) {
    super(args);
  }

  getClaimAuditGroups = async (
    input: getClaimAuditGroupsQueryVariables = {},
  ): ClientReturnType<getClaimAuditGroupsQuery["claimAuditGroups"]> => {
    const result = await this.runGqlQuery(
      getClaimAuditGroupsQueryDoc,
      input,
      "getClaimAuditGroups",
    );

    return {
      data: result.data?.claimAuditGroups ?? null,
      error: result.error ?? null,
    };
  };

  getAuditFindingRuleTypes = async (): ClientReturnType<
    getAuditFindingRuleTypesQuery["auditFindingRuleTypes"]
  > => {
    const result = await this.runGqlQuery(
      getAuditFindingRuleTypesQueryDoc,
      {},
      "getAuditFindingRuleTypes",
    );

    return {
      data: result.data?.auditFindingRuleTypes ?? null,
      error: result.error ?? null,
    };
  };

  createClaimAuditGroup = async (input: {
    input: CreateClaimAuditGroupInput;
  }): ClientReturnType<
    createClaimAuditGroupMutation["createClaimAuditGroup"]
  > => {
    const result = await this.runGqlMutation(
      createClaimAuditGroupMutationDoc,
      { input: input.input },
      "createClaimAuditGroup",
    );

    return {
      data: result.data?.createClaimAuditGroup ?? null,
      error: result.error ?? null,
    };
  };

  deleteClaimAuditGroup = async (input: {
    id: string;
  }): ClientReturnType<
    deleteClaimAuditGroupMutation["deleteClaimAuditGroup"]
  > => {
    const result = await this.runGqlMutation(
      deleteClaimAuditGroupMutationDoc,
      { id: input.id },
      "deleteClaimAuditGroup",
    );

    return {
      data: result.data?.deleteClaimAuditGroup ?? null,
      error: result.error ?? null,
    };
  };

  assignClaimsToAuditGroup = async (input: {
    input: AssignClaimsToAuditGroupInput;
  }): ClientReturnType<
    assignClaimsToAuditGroupMutation["assignClaimsToAuditGroup"]
  > => {
    const result = await this.runGqlMutation(
      assignClaimsToAuditGroupMutationDoc,
      { input: input.input },
      "assignClaimsToAuditGroup",
    );

    return {
      data: result.data?.assignClaimsToAuditGroup ?? null,
      error: result.error ?? null,
    };
  };

  unassignClaimsFromAuditGroup = async (input: {
    input: UnassignClaimsFromAuditGroupInput;
  }): ClientReturnType<
    unassignClaimsFromAuditGroupMutation["unassignClaimsFromAuditGroup"]
  > => {
    const result = await this.runGqlMutation(
      unassignClaimsFromAuditGroupMutationDoc,
      { input: input.input },
      "unassignClaimsFromAuditGroup",
    );

    return {
      data: result.data?.unassignClaimsFromAuditGroup ?? null,
      error: result.error ?? null,
    };
  };

  updateClaimAuditGroup = async (input: {
    id: string;
    patch: ClaimAuditGroupPatch;
  }): ClientReturnType<
    updateClaimAuditGroupMutation["updateClaimAuditGroup"]
  > => {
    const result = await this.runGqlMutation(
      updateClaimAuditGroupMutationDoc,
      { id: input.id, patch: input.patch },
      "updateClaimAuditGroup",
    );

    return {
      data: result.data?.updateClaimAuditGroup ?? null,
      error: result.error ?? null,
    };
  };

  getClaimAuditGroupComments = async (input: {
    id: string;
  }): ClientReturnType<AuditGroupCommentsQuery["claimAuditGroup"]> => {
    const result = await this.runGqlQuery(
      getClaimAuditGroupCommentsQueryDoc,
      { id: input.id },
      "getClaimAuditGroupComments",
    );

    return {
      data: result.data?.claimAuditGroup ?? null,
      error: result.error ?? null,
    };
  };

  createClaimAuditGroupComment = async (input: {
    input: CreateClaimAuditGroupCommentInput;
  }): ClientReturnType<
    CreateClaimAuditGroupCommentMutation["createClaimAuditGroupComment"]
  > => {
    const result = await this.runGqlMutation(
      createClaimAuditGroupCommentMutationDoc,
      { input: input.input },
      "createClaimAuditGroupComment",
    );

    return {
      data: result.data?.createClaimAuditGroupComment ?? null,
      error: result.error ?? null,
    };
  };

  createReviewedAuditFinding = async ({
    reviewedAuditFinding,
  }: {
    reviewedAuditFinding: ReviewedAuditFindingInput;
  }): ClientReturnType<
    createReviewedAuditFindingMutation["createReviewedAuditFinding"]
  > => {
    const result = await this.runGqlMutation(
      createReviewedAuditFindingMutationDoc,
      { reviewedAuditFinding },
      "createReviewedAuditFinding",
    );

    return {
      data: result.data?.createReviewedAuditFinding ?? null,
      error: result.error ?? null,
    };
  };
}
